/**
 * Shaping and glyph placement shared by every outline-rendered text layout.
 *
 * A run is shaped once per font and text (cached in font units, so resizing, auto-fit and slider
 * drags only rescale), then each glyph is dropped on a layout frame: a point on a path plus the
 * tangent there. Zero-advance glyphs (combining marks: Vietnamese tone marks typed decomposed,
 * Thai vowels…) ride on their base glyph's frame instead of getting one of their own, so a tight
 * curve cannot pull a mark off its letter.
 */
import type { FontkitFont, FontkitPath } from './fontEngine';
import type { CurvePath, PathPoint } from './pathGeometry';

export type ShapedGlyph = {
  d: string;
  path: FontkitPath;
  /** Slot length along the baseline: outline advance plus letter spacing (0 for marks). */
  advance: number;
  /** The font's own advance, scaled. */
  outline: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  /** Positioned relative to the previous base glyph rather than on its own slot. */
  mark: boolean;
};

export type ShapedRun = { glyphs: ShapedGlyph[]; total: number };

export type PlacedGlyph = { d: string; path: FontkitPath; x: number; y: number; rotation: number; scaleX?: number; scaleY?: number };

/** Where on the layout a glyph's slot midpoint lands. `scaleY` stretches the glyph vertically. */
export type GlyphFrame = PathPoint & { scaleY?: number };

export type InkBounds = { minX: number; minY: number; maxX: number; maxY: number };

type UnitGlyph = { d: string; path: FontkitPath; xAdvance: number; xOffset: number; yOffset: number; mark: boolean };

const RUN_CACHE_LIMIT = 128;
const runCache = new WeakMap<FontkitFont, Map<string, UnitGlyph[]>>();

function numberValue(value: unknown, fallback: number): number {
  const candidate = Number(value);
  return Number.isFinite(candidate) ? candidate : fallback;
}

export function unitsPerEm(font: FontkitFont): number {
  return Math.max(1, Number(font.unitsPerEm) || 1000);
}

/** Shaped glyphs in font units, cached per font and (NFC-normalized) text. */
function unitRun(font: FontkitFont, text: string): UnitGlyph[] {
  // NFC first: a decomposed "ế" (e + two combining marks) would otherwise shape to three glyphs
  // even when the font has the precomposed letter.
  const normalized = text.normalize('NFC');
  let cache = runCache.get(font);
  if (!cache) {
    cache = new Map();
    runCache.set(font, cache);
  }
  const hit = cache.get(normalized);
  if (hit) {
    cache.delete(normalized);
    cache.set(normalized, hit);
    return hit;
  }
  const run = font.layout(normalized);
  const glyphs: UnitGlyph[] = [];
  run.glyphs.forEach((glyph, index) => {
    const path = glyph.path;
    // Spaces have a path object but an empty outline; keeping them preserves their advance so
    // words do not collapse together.
    if (!path) return;
    const position = run.positions[index] ?? {};
    const xAdvance = numberValue(position.xAdvance, 0);
    glyphs.push({
      d: path.toSVG?.() ?? '',
      path,
      xAdvance,
      xOffset: numberValue(position.xOffset, 0),
      yOffset: numberValue(position.yOffset, 0),
      mark: glyphs.length > 0 && xAdvance === 0,
    });
  });
  cache.set(normalized, glyphs);
  if (cache.size > RUN_CACHE_LIMIT) cache.delete(cache.keys().next().value as string);
  return glyphs;
}

export function shapeRun(font: FontkitFont, text: string, fontSize: number, spacing: number): ShapedRun {
  const scale = fontSize / unitsPerEm(font);
  const glyphs = unitRun(font, text).map((glyph): ShapedGlyph => {
    const outline = Math.max(0, glyph.xAdvance * scale);
    return {
      d: glyph.d,
      path: glyph.path,
      advance: glyph.mark ? 0 : Math.max(0, outline + spacing),
      outline,
      offsetX: glyph.xOffset * scale,
      offsetY: glyph.yOffset * scale,
      scale,
      mark: glyph.mark,
    };
  });
  return { glyphs, total: glyphs.reduce((sum, glyph) => sum + glyph.advance, 0) };
}

/**
 * Largest font size in [min, max] whose run fits `available`.
 *
 * Run length is linear in size (`size·Σadvance/upm + n·spacing`), so this solves it directly
 * instead of re-shaping the text a dozen times per slider tick.
 */
export function fitRunSize(font: FontkitFont, text: string, spacing: number, available: number, min: number, max: number): number {
  let units = 0;
  let slots = 0;
  for (const glyph of unitRun(font, text)) {
    if (glyph.mark) continue;
    units += Math.max(0, glyph.xAdvance);
    slots += 1;
  }
  const high = Math.max(min, max);
  if (units <= 0) return high;
  const size = (available - slots * spacing) * unitsPerEm(font) / units;
  return Math.max(min, Math.min(high, size));
}

function bases(shaped: ShapedGlyph[]): ShapedGlyph[] {
  return shaped.filter((glyph) => !glyph.mark);
}

/**
 * Midpoint between the first and last glyph centres.
 *
 * Curved layouts must be centred on this, not on `total / 2`: the outer glyphs own advance slots
 * of different widths, so centring on the slots swings the two ends of the arc to different
 * heights and one side of the text reads as lower than the other.
 */
export function runCenterOf(shaped: ShapedGlyph[], total: number): number {
  const list = bases(shaped);
  if (!list.length) return total / 2;
  return (list[0].advance + total * 2 - list[list.length - 1].advance) / 4;
}

/**
 * Where a glyph's outline sits along its own baseline, relative to its point on the layout path.
 *
 * Glyph outlines start at their baseline-left origin, so a glyph dropped straight onto its own
 * midpoint runs half an advance past the point whose tangent it was rotated to: neighbours then
 * overlap or gap by half the difference of their advances, which reads as uneven letter spacing
 * and uneven tilt. Backing off half an advance centres each glyph on the path, the way curved text
 * reads in Canva. It is half the *outline* advance, so letter spacing lands half on either side of
 * the glyph rather than all of it trailing — a run of identical glyphs then stays symmetric no
 * matter how it is spaced.
 */
export function baselineOffset(glyph: ShapedGlyph): number {
  return glyph.offsetX - glyph.outline / 2;
}

function rotate(localX: number, localY: number, rotation: number): { x: number; y: number } {
  const radians = rotation * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { x: cos * localX - sin * localY, y: sin * localX + cos * localY };
}

/**
 * Drawing origin for a glyph whose slot midpoint sits at (x, y) on the layout path.
 * `shift` moves the baseline along the glyph's own down axis (used to hang text off a ring).
 */
export function glyphOrigin(glyph: ShapedGlyph, x: number, y: number, rotation: number, shift = 0): { x: number; y: number } {
  const local = rotate(baselineOffset(glyph), -glyph.offsetY + shift, rotation);
  return { x: x + local.x, y: y + local.y };
}

/** Where a glyph's ink sits around the point of the layout path it was placed on. */
export function inkAround(glyph: ShapedGlyph, shift = 0): { left: number; right: number; top: number; bottom: number } {
  const localX = baselineOffset(glyph);
  const localY = -glyph.offsetY + shift;
  const box = glyph.path?.bbox;
  const usable = box && [box.minX, box.minY, box.maxX, box.maxY].every((value) => Number.isFinite(value));
  if (!usable) return { left: localX, right: localX, top: localY, bottom: localY };
  // Glyph outlines are drawn with the y axis flipped, so the outline's top edge is its max y.
  return {
    left: localX + box.minX * glyph.scale,
    right: localX + box.maxX * glyph.scale,
    top: localY - box.maxY * glyph.scale,
    bottom: localY - box.minY * glyph.scale,
  };
}

export function transformedGlyphBounds(glyph: PlacedGlyph): InkBounds {
  const radians = glyph.rotation * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const scaleX = glyph.scaleX ?? 1;
  const scaleY = -1 * (glyph.scaleY ?? 1);
  return glyph.path.transform(cos * scaleX, sin * scaleX, -sin * scaleY, cos * scaleY, glyph.x, glyph.y).bbox;
}

/**
 * How far a glyph's outline reaches away from the apex of a curved run.
 *
 * Measured on the transformed outline rather than a rotated box: the corners of an 'A' or a 'W' box
 * carry no ink, so a box would claim a reach the letter does not have.
 */
function outwardReach(glyph: ShapedGlyph, point: PathPoint, outward: number, shift: number): number {
  const box = transformedGlyphBounds({
    d: glyph.d,
    path: glyph.path,
    ...glyphOrigin(glyph, point.x, point.y, point.rotation, shift),
    rotation: point.rotation,
    scaleX: glyph.scale,
    scaleY: glyph.scale,
  });
  return outward > 0 ? box.maxY : -box.minY;
}

/**
 * Arc-length offset of the apex that leaves both ends of a curved run equally deep.
 *
 * Advance slots cannot balance a curve: at the ends of an arc a glyph is rotated by roughly a
 * quarter turn, so its *width* becomes its vertical reach and a wide 'A' dives far below a narrow
 * 't'. The bounding box then hugs the 'A' while the 't' keeps a visible gap that swaps sides with
 * the reverse toggle. Seeding on the outer ink edges cancels most of that, and a short bisection
 * inside half an end glyph closes the rest without letting the run drift off centre.
 */
export function balancedRunCenter(shaped: ShapedGlyph[], total: number, project: CurvePath, outward: number, shift = 0): number {
  const list = bases(shaped);
  if (list.length < 2) return runCenterOf(shaped, total);
  const first = list[0];
  const last = list[list.length - 1];
  const firstMid = first.advance / 2;
  const lastMid = total - last.advance / 2;
  const firstInk = inkAround(first, shift);
  const lastInk = inkAround(last, shift);
  const seed = (firstMid + firstInk.left + lastMid + lastInk.right) / 2;
  const imbalance = (runCenter: number) =>
    outwardReach(first, project(firstMid - runCenter), outward, shift) -
    outwardReach(last, project(lastMid - runCenter), outward, shift);

  const reach = (firstInk.right - firstInk.left + lastInk.right - lastInk.left) / 4;
  let low = seed - reach;
  let high = seed + reach;
  const lowValue = imbalance(low);
  if ((lowValue > 0) === (imbalance(high) > 0)) {
    return [seed, low, high].reduce((best, candidate) =>
      Math.abs(imbalance(candidate)) < Math.abs(imbalance(best)) ? candidate : best);
  }
  for (let iteration = 0; iteration < 24; iteration += 1) {
    const middle = (low + high) / 2;
    if ((imbalance(middle) > 0) === (lowValue > 0)) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

/**
 * Drop every glyph of a run on its frame. `frameAt` receives the slot midpoint along the run;
 * marks reuse their base glyph's frame and sit where the font's mark positioning puts them.
 */
export function placeRun(shaped: ShapedGlyph[], frameAt: (middle: number, glyph: ShapedGlyph) => GlyphFrame, shift = 0): PlacedGlyph[] {
  const placed: PlacedGlyph[] = [];
  let cursor = 0;
  let base: { glyph: ShapedGlyph; frame: GlyphFrame } | null = null;
  for (const glyph of shaped) {
    if (glyph.mark && base) {
      // The pen after the base glyph sits half its outline advance past the slot midpoint; the
      // font positions the mark from there.
      const local = rotate(base.glyph.outline / 2 + glyph.offsetX, -glyph.offsetY + shift, base.frame.rotation);
      placed.push({
        d: glyph.d, path: glyph.path, x: base.frame.x + local.x, y: base.frame.y + local.y,
        rotation: base.frame.rotation, scaleX: glyph.scale, scaleY: glyph.scale * (base.frame.scaleY ?? 1),
      });
      continue;
    }
    const frame = frameAt(cursor + glyph.advance / 2, glyph);
    const origin = glyphOrigin(glyph, frame.x, frame.y, frame.rotation, shift);
    placed.push({ d: glyph.d, path: glyph.path, x: origin.x, y: origin.y, rotation: frame.rotation, scaleX: glyph.scale, scaleY: glyph.scale * (frame.scaleY ?? 1) });
    base = { glyph, frame };
    cursor += glyph.advance;
  }
  return placed;
}

/** Place a run along a path, its `runCenter` landing on the path's anchor. */
export function placeOnPath(shaped: ShapedGlyph[], path: CurvePath, runCenter: number, shift = 0): PlacedGlyph[] {
  return placeRun(shaped, (middle) => path(middle - runCenter), shift);
}

export function emptyInkBounds(): InkBounds {
  return { minX: Number.POSITIVE_INFINITY, minY: Number.POSITIVE_INFINITY, maxX: Number.NEGATIVE_INFINITY, maxY: Number.NEGATIVE_INFINITY };
}

/** Union of the painted outlines; spaces carry no ink and do not vote. */
export function runInkBounds(glyphs: PlacedGlyph[]): InkBounds {
  const box = emptyInkBounds();
  for (const glyph of glyphs) {
    if (!glyph.d) continue;
    const bounds = transformedGlyphBounds(glyph);
    box.minX = Math.min(box.minX, bounds.minX);
    box.minY = Math.min(box.minY, bounds.minY);
    box.maxX = Math.max(box.maxX, bounds.maxX);
    box.maxY = Math.max(box.maxY, bounds.maxY);
  }
  return box;
}

export function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

export function glyphMarkup(glyphs: PlacedGlyph[], offsetX = 0, offsetY = 0): string {
  return glyphs.filter((glyph) => glyph.d).map((glyph) => {
    const transform = `translate(${(glyph.x + offsetX).toFixed(3)} ${(glyph.y + offsetY).toFixed(3)}) rotate(${glyph.rotation.toFixed(3)}) scale(${(glyph.scaleX ?? 1).toFixed(4)} ${(-1 * (glyph.scaleY ?? 1)).toFixed(4)})`;
    return `<path d="${escapeXml(glyph.d)}" transform="${transform}"/>`;
  }).join('');
}

/** Cap height in px for a shaped run's font size, used to hang text off a ring. */
export function capHeight(font: FontkitFont, fontSize: number): number {
  const units = Number((font as { capHeight?: number }).capHeight) || Number(font.ascent) * 0.7 || unitsPerEm(font) * 0.7;
  return units * fontSize / unitsPerEm(font);
}
